import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { Badge, Button, Tag } from 'antd';
import moment from 'moment';
import { getTemperatureData, generateBreachesReport, getLoadingHistory } from 'store/pageSlice';
import RCTable from 'components/custom-table/RCTable';
import { Flex } from 'atoms';
import Switcher from './Switcher';
import { ACL } from 'components/common';
import LoadingHistoryModal from 'components/LoadingHistoryModal';
function TemperatureTab({ tripId }) {
	const dispatch = useDispatch();
	const [showBreachesOnly, setShowBreachesOnly] = useState(false);
	const [loadingHistoryModalVisible, setLoadingHistoryModalVisible] = useState(false);
	const [ModalData, setModalData] = useState(null);
	const columns = [
		{
			title: 'Time Stamp',
			dataIndex: 'timestamp',
			key: 'timestamp',
			render: (text) =>
				moment(text, 'DD/MM/YY HH:mm:ss')
					.add(5, 'hours')
					.add(30, 'minutes')
					.format('DD/MM/YYYY HH:mm:ss'),
		},
		{
			title: 'Latitude',
			dataIndex: 'latitude',
			key: 'latitude',
			render: (text) => parseFloat(text).toFixed(6),
		},
		{
			title: 'Longitude',
			dataIndex: 'longitude',
			key: 'longitude',
			render: (text) => parseFloat(text).toFixed(6),
		},
		{
			title: 'Temp Range',
			dataIndex: 'temp_range',
			key: 'temp_range',
			render: (text) => `${text.slice(0, 4)}°C`,
		},
		{
			title: 'Temperature',
			dataIndex: 'temperature',
			key: 'temperature',
			render: (text, record) => (
				<span
					style={{
						color: record.temperature_breach ? 'red' : 'green',
						fontWeight: record.temperature_breach ? 'bold' : 'normal',
					}}
				>
					{text}°C
				</span>
			),
			sorter: (a, b) => a.temperature - b.temperature,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (text) => <Badge status={text === '-' ? 'default' : 'success'} text={text} />,
		},
		{
			title: 'Loading Status',
			dataIndex: 'loading_status',
			key: 'loading_status',
			render: (text) => {
				let color = 'default';
				if (text === 'loaded') color = 'green';
				else if (text === 'unloaded') color = 'orange';
				return <Tag color={color}>{text}</Tag>;
			},
		},
		{
			title: 'Load Time Stamp',
			dataIndex: 'load_timestamp',
			key: 'load_timestamp',
			render: (text) =>
				text
					? moment(text).add(5, 'hours').add(30, 'minutes').format('DD/MM/YYYY HH:mm:ss')
					: '-',
		},
	];
	const [temperatureData, setTemperatureData] = useState({ rows: [], count: 0 });
	const [loadingHistory, setLoadingHistory] = useState({ data: {}, count: 0 });
	const [paginateConfig, setPaginateConfig] = useState({
		limit: 10,
		offset: 0,
		currentPage: 1,
		sort_column: 'timestamp',
		sort_direction: 'DESC',
	});

	// Keep a stable reference of breach_only to avoid dispatchAction identity changes
	const breachOnlyRef = useRef(showBreachesOnly);
	useEffect(() => {
		breachOnlyRef.current = showBreachesOnly;
	}, [showBreachesOnly]);

	useEffect(() => {
		dispatch(getLoadingHistory({ trip_id: tripId })).then((res) => {
			setLoadingHistory(res);
		});
		dispatch(getTemperatureData({ trip_id: tripId })).then((res) => {
			const loadStatusHistory = res?.rows[0]?.load_status_table || [];
			setModalData(loadStatusHistory);
		});
	}, [dispatch, tripId]);

	const temperatureAction = React.useCallback(
		(params) =>
			getTemperatureData({
				trip_id: tripId,
				breach_only: breachOnlyRef.current,
				...params,
			}),
		[tripId]
	);

	// Removed paginateConfig effect to avoid double triggering fetch; we update it inline on toggle

	const exportExcel = () => {
		dispatch(
			generateBreachesReport({
				trip_id: tripId,
				file_type: 'csv',
				breach_only: showBreachesOnly,
			})
		);
	};

	const exportPDF = () => {
		dispatch(
			generateBreachesReport({
				trip_id: tripId,
				file_type: 'pdf',
				breach_only: showBreachesOnly,
			})
		);
	};
	return (
		<>
			<div
				style={{
					marginTop: '10px',
					backgroundColor: 'white',
					padding: '10px',
					borderRadius: '10px',
					height: '100px',
				}}
			>
				<Flex>
					<Flex justify="space-between" align="center" style={{ width: '100%' }}>
						<div
							style={{
								marginRight: '20px',
								marginLeft: '20px',
								color: '#403E3E',
								marginTop: '10px',
							}}
						>
							Loading History
							<div style={{ lineHeight: '3', color: 'blue', fontWeight: 'bold' }}>
								<Button
									type="link"
									onClick={() => setLoadingHistoryModalVisible(true)}
									style={{
										color: 'blue',
										fontWeight: 'bold',
										padding: 0,
										height: 'auto',
										fontSize: 'inherit',
									}}
								>
									{loadingHistory?.data?.loaded_state_count || '-'}
								</Button>
							</div>
						</div>
						<div
							style={{
								marginRight: '20px',
								marginLeft: '20px',
								color: '#403E3E',
								marginTop: '10px',
							}}
						>
							Temperature Breaches
							<div style={{ lineHeight: '3', color: 'red', fontWeight: 'bold' }}>
								{loadingHistory?.data?.breach_count || '-'}
							</div>
						</div>
						<div
							style={{
								marginRight: '20px',
								marginLeft: '20px',
								color: '#403E3E',
								marginTop: '10px',
							}}
						>
							Avg Breach Rate
							<div style={{ lineHeight: '3', color: 'red', fontWeight: 'bold' }}>
								{loadingHistory?.data?.average_breach_rate
									? `${loadingHistory.data.average_breach_rate}%`
									: '-'}
							</div>
						</div>
					</Flex>
					<Flex>
						<div style={{ color: '#403E3E', marginTop: '34px', whiteSpace: 'nowrap' }}>
							Breaches Only
						</div>
						<div
							style={{
								marginTop: '20px',
								marginLeft: '20px',
								marginRight: '20px',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<Switcher
								onChange={() => {
									const next = !showBreachesOnly;
									setShowBreachesOnly(next);
									setPaginateConfig((prev) => ({
										...prev,
										breach_only: next,
										offset: 0,
										currentPage: 1,
									}));
									console.log('Show breaches only:', next);
								}}
							/>
						</div>
						<ACL action="generate_report" module="temperature_history_report">
							<Button
								style={{
									marginLeft: '20px',
									marginRight: '20px',
									backgroundColor: 'white',
									color: 'black',
									border: '1px solid #000',
									marginTop: '25px',
								}}
								onClick={() => {
									exportExcel();
								}}
								icon={<PlusOutlined />}
							>
								Export Excel
							</Button>
							<Button
								style={{
									marginLeft: '20px',
									marginRight: '20px',
									backgroundColor: 'white',
									color: 'black',
									border: '1px solid #000',
									marginTop: '25px',
								}}
								icon={<DownloadOutlined />}
								onClick={() => {
									exportPDF();
								}}
							>
								Export PDF
							</Button>
						</ACL>
					</Flex>
				</Flex>
			</div>
			<div className="mt-4" style={{ marginTop: '10px', padding: '10px' }}>
				<RCTable
					rowKey="timestamp"
					columns={columns}
					tableDatas={[temperatureData, setTemperatureData]}
					togglePaginate={[paginateConfig, setPaginateConfig]}
					dispatchAction={temperatureAction}
					hasPagination
					pagination={{
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
					}}
					configs={{
						defaultSort: 'DESC',
						defaultsortField: 'timestamp',
					}}
				/>
			</div>
			<LoadingHistoryModal
				visible={loadingHistoryModalVisible}
				onClose={() => setLoadingHistoryModalVisible(false)}
				loadStatusHistory={ModalData || []}
			/>
		</>
	);
}

export default TemperatureTab;

TemperatureTab.propTypes = {
	tripId: PropTypes.string.isRequired,
};
